import { buildImageProxyUrl } from '@repo/utils/url';
import { format, startOfToday } from 'date-fns';

import userApi from '../api/user';

/** @knipignore - used in website */
export enum ShareType {
  Link = 'Link',
  Email = 'Email',
  Mail = 'Mail',
  Facebook = 'Facebook',
  Pinterest = 'Pinterest',
  X = 'X',
  Twitter = 'Twitter',
  WhatsApp = 'WhatsApp',
  LinkedIn = 'LinkedIn',
}

/**
 * Options to create general share links
 */
interface ShareOptions {
  /**
   * subject in mails
   */
  mailSubject?: string;
  /**
   * body in mails. share link will be added to the end
   */
  mailBody?: string;
  /**
   * a preview can be used for sharing on pinterest
   * preview will be turned into a full url
   */
  preview?: string;
  /**
   * when a design is shared, justCreated can be added to the link share
   * this will show a toast when opening the link
   */
  justCreated?: boolean;
  title?: string;
}

/**
 * Options to share the design
 */
interface ShareDesignOptions {
  /**
   * title can be used as subject in mails
   */
  title?: string;
  /**
   * a preview can be used for sharing on pinterest
   * preview will be turned into a full url
   */
  preview?: string;
  /**
   * when a design is shared, justCreated can be added to the link share
   * this will show a toast when opening the link
   */
  justCreated?: boolean;
  designId?: string;
}

const buildFacebookURL = (link: string): string =>
  encodeURI(
    `https://www.facebook.com/dialog/share?app_id=${
      process.env.REACT_APP_FACEBOOK_APP_ID ||
      process.env.NEXT_PUBLIC_FACEBOOK_APP_ID
    }&display=page&href=${link}&redirect_uri=${
      process.env.REACT_APP_WEBSITE_URL || process.env.NEXT_PUBLIC_WEBSITE_URL
    }`
  );

const buildEMailURL = (link: string, subject?: string, body?: string): string =>
  `mailto:?subject=${subject ? subject : ''}&body=${
    body ? body : ''
  }${encodeURI(link)}`;

const buildWhatsappURL = (link: string): string =>
  `https://web.whatsapp.com/send?text=${encodeURI(link)}`;

const buildPinterestURL = (
  link: string,
  title: string | undefined,
  preview: string | undefined
): string => {
  const url = new URL('https://www.pinterest.com/pin/create/link/');
  const kittlLink = new URL(link);
  const currentDate = format(startOfToday(), 'yy-MM');

  const utmPinterest = {
    medium: 'organic',
    source: 'pinterest',
    campaign: `${currentDate}-pinterest_template`,
  };

  Object.keys(utmPinterest).forEach((key) => {
    const utmKey = key as keyof typeof utmPinterest;
    const keyValue = utmPinterest[utmKey];

    if (keyValue) {
      kittlLink.searchParams.append(`utm_${key}`, keyValue);
    }
  });

  const resultLink = encodeURIComponent(kittlLink.toString());

  if (preview) {
    url.searchParams.append('media', encodeURI(preview));
  }

  if (title) {
    url.searchParams.append('description', title);
  }

  url.searchParams.append('url', resultLink);

  return url.toString();
};

const buildTwitterURL = (link: string): string =>
  `https://twitter.com/intent/tweet?url=${encodeURI(link)}`;

const buildLinkedinUrl = (link: string): string =>
  `https://www.linkedin.com/sharing/share-offsite/?url=${link}`;

/**
 * build a link for sharing
 */
/** @knipignore */
export const getShareLink = (
  shareType: ShareType,
  link: string,
  shareOptions: ShareOptions = {}
): string => {
  switch (shareType) {
    case 'Link':
      return `${link}${shareOptions.justCreated ? '?created=true' : ''}`;
    case 'Email':
    case 'Mail':
      return buildEMailURL(
        link,
        shareOptions.mailSubject,
        shareOptions.mailBody
      );
    case 'Facebook':
      return buildFacebookURL(link);
    case 'Pinterest':
      return buildPinterestURL(
        link,
        shareOptions.title,
        shareOptions.preview &&
          buildImageProxyUrl(shareOptions.preview, {
            width: 1200,
            height: 630,
            format: 'jpg',
          })
      );
    case 'X':
    case 'Twitter':
      return buildTwitterURL(link);
    case 'WhatsApp':
      return buildWhatsappURL(link);
    case 'LinkedIn':
      return buildLinkedinUrl(link);
    default:
      console.warn(
        `Got request to build a link for unsupported share type '${shareType}'.`
      );
      return '';
  }
};

interface HandleShareParams {
  type: ShareType;
  options: ShareDesignOptions;
  preview: string;
}

/**
 * Handle sharing a design (not editable).
 */
export const handleSocialShare = async ({
  type,
  options,
  preview,
}: HandleShareParams): Promise<{
  url: string;
  target: Window | null | true;
}> => {
  const share = await userApi.shareDesign({
    designId: options.designId,
    preview,
    editable: false,
    email: undefined,
    user: undefined,
  });
  if ('error' in share) {
    return { url: '', target: null };
  }
  const url = getShareLink(type, share.link, {
    mailSubject: `Kittl - ${options.title}`,
    preview: `api/${share.preview}`,
    justCreated: true,
    title: options.title,
  });

  const noopener = type === 'Link' ? [] : ['noopener'];
  const target = window.open(url, '_blank', ...noopener);

  return { url, target: type === 'Link' ? target : true };
};
